import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type PointerEvent as ReactPointerEvent,
} from 'react';

function clampWidth(width: number, minWidth: number, maxWidth: number): number {
  return Math.min(maxWidth, Math.max(minWidth, width));
}

export type UseDrawerWidthResizeOptions = {
  initialWidth: number;
  minWidth: number;
  maxWidth: number;
  onWidthChange?: (width: number) => void;
  onResizeEnd?: (width: number) => void;
};

export function useDrawerWidthResize({
  initialWidth,
  minWidth,
  maxWidth,
  onWidthChange,
  onResizeEnd,
}: UseDrawerWidthResizeOptions) {
  const [width, setWidth] = useState(() => clampWidth(initialWidth, minWidth, maxWidth));
  const [isResizing, setIsResizing] = useState(false);
  const dragRef = useRef({ startX: 0, startWidth: width });
  const widthRef = useRef(width);
  const isResizingRef = useRef(false);
  const prevInitialWidthRef = useRef(initialWidth);
  const prevMaxWidthRef = useRef(maxWidth);
  const prevMinWidthRef = useRef(minWidth);

  useEffect(() => {
    widthRef.current = width;
  }, [width]);

  useEffect(() => {
    isResizingRef.current = isResizing;
  }, [isResizing]);

  // Reset when the caller changes the intended starting width.
  useEffect(() => {
    if (prevInitialWidthRef.current === initialWidth) {
      return;
    }
    prevInitialWidthRef.current = initialWidth;
    prevMaxWidthRef.current = maxWidth;
    prevMinWidthRef.current = minWidth;
    const next = clampWidth(initialWidth, minWidth, maxWidth);
    setWidth(next);
    widthRef.current = next;
  }, [initialWidth, minWidth, maxWidth]);

  // When viewport-driven max/min change, reclamp current width.
  // If the drawer was at the previous max, stay pinned to the new max (97vw).
  useEffect(() => {
    const maxChanged = prevMaxWidthRef.current !== maxWidth;
    const minChanged = prevMinWidthRef.current !== minWidth;
    if (!maxChanged && !minChanged) {
      return;
    }

    const prevMax = prevMaxWidthRef.current;
    prevMaxWidthRef.current = maxWidth;
    prevMinWidthRef.current = minWidth;

    setWidth((current) => {
      const wasAtMax = current >= prevMax - 1;
      const next = wasAtMax
        ? clampWidth(maxWidth, minWidth, maxWidth)
        : clampWidth(current, minWidth, maxWidth);
      widthRef.current = next;
      return next;
    });
  }, [minWidth, maxWidth]);

  const updateWidth = useCallback(
    (nextWidth: number) => {
      const clamped = clampWidth(nextWidth, minWidth, maxWidth);
      setWidth(clamped);
      widthRef.current = clamped;
      onWidthChange?.(clamped);
    },
    [maxWidth, minWidth, onWidthChange],
  );

  const handleResizePointerDown = useCallback(
    (event: ReactPointerEvent<HTMLButtonElement>) => {
      event.preventDefault();
      event.currentTarget.setPointerCapture(event.pointerId);
      dragRef.current = { startX: event.clientX, startWidth: widthRef.current };
      isResizingRef.current = true;
      setIsResizing(true);
    },
    [],
  );

  const endResize = useCallback(() => {
    if (!isResizingRef.current) {
      return;
    }
    isResizingRef.current = false;
    setIsResizing(false);
    onResizeEnd?.(widthRef.current);
  }, [onResizeEnd]);

  useEffect(() => {
    if (!isResizing) {
      return undefined;
    }

    const handlePointerMove = (event: PointerEvent) => {
      const { startX, startWidth } = dragRef.current;
      const delta = startX - event.clientX;
      updateWidth(startWidth + delta);
    };

    const handlePointerEnd = () => {
      endResize();
    };

    window.addEventListener('pointermove', handlePointerMove);
    window.addEventListener('pointerup', handlePointerEnd);
    window.addEventListener('pointercancel', handlePointerEnd);

    return () => {
      window.removeEventListener('pointermove', handlePointerMove);
      window.removeEventListener('pointerup', handlePointerEnd);
      window.removeEventListener('pointercancel', handlePointerEnd);
    };
  }, [endResize, isResizing, updateWidth]);

  return {
    width,
    isResizing,
    handleResizePointerDown,
    endResize,
  };
}
